import { parseHTML } from 'linkedom';
import { ParseError } from './errors';

/**
 * Parse an HTML string into a DOM that supports a `document.querySelector`-like
 * API. We use `linkedom` because it works in the browser (`cheerio` is
 * Node-only) and because it doesn't run `<script>` tags — perfect for
 * scraping untrusted pages.
 */
export function parseDocument(html: string): Document {
  const { document } = parseHTML(html);
  return document as unknown as Document;
}

/**
 * `querySelector` that throws a `ParseError` if the element isn't found.
 * Use for selectors that are load-bearing for parsing correctness.
 */
export function selectOrThrow<E extends Element = Element>(
  root: ParentNode,
  selector: string,
  context?: { source?: string; snippet?: string },
): E {
  const el = root.querySelector(selector) as E | null;
  if (!el) {
    throw new ParseError(`Selector did not match: ${selector}`, context);
  }
  return el;
}

/** Best-effort text extraction with whitespace collapsed. */
export function textOf(el: Element | null | undefined): string {
  if (!el) return '';
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** Parse an integer with a fallback. Strips commas. */
export function toInt(input: string | null | undefined, fallback = 0): number {
  if (!input) return fallback;
  const n = Number.parseInt(input.replace(/[,\s]/g, ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Resolve a (possibly relative) URL against a base. Returns the original
 * string if it doesn't parse — `linkedom` sometimes hands us empty strings.
 */
export function absoluteUrl(href: string, base: string): string {
  try {
    return new URL(href, base).toString();
  } catch {
    return href;
  }
}
